<template>
<div class="help">

  <top-nav />

  <el-row>
    <el-col :offset="4" :span="14">

      <div class="text-title">
        What is Allostery
      </div>

      <p>A perturbation of a specific site on a protein's surface by ligand binding, light absorption, or post-translational modification can propagate to distant functional sites within a protein's structure and affect its function. This allosteric phenomenon has been widely observed in various biological processes, such as regulations of gene transcription and enzyme activity, as well as cell signaling.</p>

      <p>Computational approaches to deciphering allosteric coupling have attracted significant attention as they provide inexpensive opportunities to better understand and control protein function and cellular activity, as well as to establish new therapeutic strategies. We propose that allosteric phenomenon is a general property of heterogeneous media, whereby the coupling between distal sites of the heterogeneous material propagates through regions of higher coupling density. Packing heterogeneities in proteins result in specific propagation of perturbations between distal sites within the structure, thus resulting in allosteric coupling.</p>

      <div class="text-title">
        What is Ohm
      </div>

      <p>We develop a computationally efficient network-based method, Ohm, for finding and characterizing allosteric communication network within a protein that unlike previously developed simulations-based approaches, is based solely on a protein structure.</p>

      <div>
        Ohm provides four types of protein allostery analyses:
        <ol>
          <li>Allosteric site prediction</li>
          <li>Allosteric pathways prediction</li>
          <li>Critical allosteric residues identification</li>
          <li>Inter-residue allosteric correlations prediction</li>
        </ol>
      </div>

      <div class="text-title">
        How to predict allosteric sites?
      </div>

      <h3>Task Submission</h3>

      <h4>Step 1. Upload the tertiary structure.</h4>
      <p>
        The prediction of allosteric site is based solely on the tertiary structure of the protein of interest.
        We provide a convenient tool for users to fetch the tertiary structure directly in Ohm website.
        Ohm will automatically search through the RCSB database in the back-end, according to the PDB ID or protein name provided by the user.
        Of course, users could also choose to download, edit, and upload the tertiary structure on their own.
      </p>

      <div style="text-align:center">
        <p>Search the tertiary structure by protein name</p>
        <img align="middle" src="/static/ohm-select-structure.gif">
      </div>

      <div style="text-align:center">
        <p>Upload the tertiary structure</p>
        <img src="/static/ohm-upload-structure.gif">
      </div>

      <h4>Step 2. Designate the active site.</h4>
      <p>
        Ohm will calculate the allosteric coupling intensity (ACI) of each residue in the protein structure.
        ACI hotspots on the surface of the protein structure are potential allosteric sites.
        The calculation of ACI needs the user to designate the position of the active site.
        We provide a residue selection component to help choose the residues in the active site:
      </p>

      <div style="text-align:center">
        <p>Set the active site through the sequence panel.</p>
        <img src="/static/ohm-set-active-site.gif">
      </div>

      <div style="text-align:center">
        <p>Set the active site through NglView.</p>
        <img src="/static/ohm-set-active-site-ngl.gif">
      </div>

      <h4>Step 3. Set the parameters.</h4>
      <p>
        Before submitting the task, there are several parameters that need to be set.
        The first one is the title of the task, which could help the user to remember the purpose of the task.
        The second parameter is the distance cutoff for the calculation of contacts in protein structures. The distance cutoff is set to 3.4 &#8491; by default.
        The third parameter is 'alpha', which dictates the magnitude of ACI. The default value of 'alpha' is set to 3.
      </p>

      <div style="text-align:center">
        <p>Set parameters and submit.</p>
        <img src="/static/ohm-set-parameters.gif">
      </div>

      <h3>Results Analyzaation</h3>

      <h4>ACI bar chart</h4>

      <p>
        ACI of all residues relative to the active site are calculated and plotted as a bar chart.
        X-axis refers to the residues ordered in sequence; Y-axis refers to ACI.
      </p>

      <div style="text-align:center">
        <p>ACI Bar Chart.</p>
        <img src="/static/ohm-task1-bar.gif">
      </div>

      <h4>Protein tertiary structure rendered by ACI</h4>

      <p>
        We have also rendered the 3D structure of the protein of interest by ACI.
        By setting the representation type as 'surface', users could readily identify positions of ACI hotspots, which are potential allosteric sites.
      </p>

      <div style="text-align:center">
        <p>Tertiary structure rendered by ACI.</p>
        <img src="/static/ohm-task1-ngl.gif">
      </div>

      <div class="text-title">
        How to identify allosteric pathways and critical allosteric residues?
      </div>

      <h3>Task Submission</h3>

      <h4>Step 1. Upload the tertiary structure.</h4>
      <p>
        The identification of allosteric pathways and critical residues is also based soley on the 3D structure of the protein of interest.
        The difference with the analysis of allosteric site prediction is that both the position of the active site and the allosteric site are needed.
        Users still could fetch the tertiary structure of protein of interest in Ohm, or upload its tertiary structure on their own.
      </p>

      <div style="text-align:center">
        <p>Search the tertiary structure by protein name</p>
        <img align="middle" src="/static/ohm-task2-select-structure.gif">
      </div>

      <div style="text-align:center">
        <p>Upload the tertiary structure</p>
        <img src="/static/ohm-task2-upload-structure.gif">
      </div>

      <h4>Step 2. Designate the active site and allosteric site.</h4>
      <p>
        Ohm will calculate the allosteric coupling intensity (ACI) of each residue in the protein structure. ACI hotspots are all potential allosteric site.
      </p>

      <div style="text-align:center">
        <p>Designate the active site and the allosteric site in the sequence panel</p>
        <img src="/static/ohm-task2-set-sites.gif">
      </div>

      <div style="text-align:center">
        <p>Designate the active site and the allosteric site through NglView</p>
        <img src="/static/ohm-task2-set-sites-ngl.gif">
      </div>

      <h4>Step 3. Set the parameters.</h4>

      <div style="text-align:center">
        <p>Set parameters and submit.</p>
        <img src="/static/ohm-set-parameters.gif">
      </div>

      <h3>Results Analyzation</h3>

      <h4>Allosteric Pathways</h4>

      <div style="text-align:center">
        <p>Allosteric pathways.</p>
        <img src="/static/ohm-task2-pathways.gif">
      </div>

      <h4>Weights of Allosteric Pathways</h4>

      <div style="text-align:center">
        <p>Weights of allosteric pathways.</p>
        <img src="/static/ohm-task2-weights.png">
      </div>

      <h4>Critical Allosteric Residues</h4>

      <div style="text-align:center">
        <p>Critical residues.</p>
        <img src="/static/ohm-task2-network.gif">
      </div>

      <div class="text-title">
        How to inter-residue allosteric correlations
      </div>

      <h3>Task Submission</h3>

      <h4>Step 1. Upload the tertiary structure.</h4>
      <p>
        The prediction of allosteric site is based solely on the tertiary structure of the protein of interest.
        We provide a convenient tool for users to fetch the tertiary structure directly in Ohm website. Ohm will automatically through the RCSB database in the back-end according to the PDB ID or protein name provided by the user. Of course, users could also choose to download, edit, and upload the tertiary structure on their own.
      </p>

      <div style="text-align:center">
        <p>Search the tertiary structure by protein name</p>
        <img align="middle" src="/static/ohm-task3-select-structure.gif">
      </div>

      <div style="text-align:center">
        <p>Upload the tertiary structure</p>
        <img src="/static/ohm-task3-upload-structure.gif">
      </div>

      <h4>Step 2. Set the parameters.</h4>

      <div style="text-align:center">
        <p>Set parameters and submit.</p>
        <img src="/static/ohm-set-parameters.gif">
      </div>

      <h3>Results Analyzation</h3>

      <h4>Inter-residue correlations</h4>

      <div style="text-align:center">
        <p>Inter-residue correlations</p>
        <img src="/static/ohm-task3-matrix.gif">
      </div>

      <h4>Dendrogram of residues clustered by inter-residue correlations</h4>

      <div style="text-align:center">
        <p>Dendrogram of residues clustered by inter-residue correlations</p>
        <img src="/static/ohm-task3-dendrogram.gif">
      </div>

      <h4>3D structure rendered by clusters of inter-residue correlations</h4>

      <div style="text-align:center">
        <p>3D structure rendered by clusters of inter-residue correlations</p>
        <img src="/static/ohm-task3-ngl.png">
      </div>

    </el-col>
  </el-row>
</div>
</template>

<script>
import { bus } from '../bus.js'
import axios from 'axios'
import TopNav from './TopNav.vue'

export default {
  name: 'Help',

  data () {
    return {
      timelineVisible: false,
      content: ''
    }
  },

  components: {
    TopNav
  },

  methods: {
  },

  mounted () {
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="scss" scoped>
@import "../styles/theme.scss";


.content {
  width: $content-width;
  margin: 20px auto;
  color: #5a5e66;
  font-size: 20px;
  text-align: justify;
}

.text-title {
  font-size: 25px;
  font-weight: 550;
  padding: 20px 0px;
  margin: 20px 0px;
  border-bottom: 1px solid rgb(221, 221, 221);
  color: #273849;
}

.help {
  font-size: 16px;
  color: rgb(48, 68, 85);
  line-height: 25.6px;
}

img {
  width: 50%;
}

.box {
  border: 1px solid #eeeeee;
  margin: 20px 0px;
  position: relative;
}

.box-head {
  padding: 10px;
  border-bottom: 1px solid #eeeeee;
  background-color: $theme-color;
  font-weight: 700;
  color: white;
}

.box-tool {
  padding:10px;
  border-bottom:1px solid #eeeeee
}

.box-content {
  padding: 10px;
}

</style>
